import {
  SortableListWithChildren,
  Sortable,
  InfoBar,
  useValidation,
} from "@etframework/ui.components";
import {
  forwardRef,
  Fragment,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { FieldTypeInputTemplate } from "./FieldTypeInputTemplate";
import { useTranslation } from "react-i18next";
import { FieldTypeContainer } from "./FieldTypeContainer";
import { useParams } from "react-router-dom";
import { useTaskType } from "../../data/queries/taskTypeQueries";
import { useEffect } from "react";

export const FieldTypeSortableList = forwardRef(
  ({ customFields = [], ...rest }, ref) => {
    const { t } = useTranslation(["CustomFieldGroup"], {
      useSuspense: false,
    });

    const componentRef = useRef(null);
    const [inputValues, setInputValues] = useState(customFields);
    const { setValidationErrors } = useValidation();

    const { taskTypeId } = useParams();
    const { data: taskType } = useTaskType(taskTypeId);

    let count = useRef(0);

    const handleInputChange = (id, value, fieldType, index) => {
      setInputValues((prev) => {
        const copy = [...prev];
        copy[index].Label = value;
        copy[index].Type = fieldType.Type;
        return copy;
      });
    };

    const handleFieldTypeClick = (fieldType) => {
      setInputValues((prev) => [
        ...prev,
        {
          Id: customFields?.Id ?? `Item-${count.current}`,
          Type: fieldType.value,
          FieldType: fieldType,
        },
      ]);
      count.current += 1;
    };

    const getDuplicateLabelTypeErrors = (fields) => {
      const map = new Map();
      const errors = {};

      fields.forEach((field, index) => {
        if (!field.Label?.trim()) return;

        const key = field.Label.trim().toLowerCase() + "|" + field.Type;
        if (map.has(key)) {
          errors[`CustomFields[${index}].Label`] = [t("DuplicatesNotAllowed")];
          const firstIndex = map.get(key);
          if (!errors[`CustomFields[${firstIndex}].Label`]) {
            errors[`CustomFields[${firstIndex}].Label`] = [
              t("DuplicatesNotAllowed"),
            ];
          }
        } else {
          map.set(key, index);
        }
      });

      return { errors: errors };
    };

    useEffect(() => {
      const errors = getDuplicateLabelTypeErrors(inputValues);
      setValidationErrors(errors);
    }, [inputValues]);

    useImperativeHandle(
      ref,
      () => ({
        handleFieldTypeClick,
        setInputValues,
        getInputValues: () => inputValues,
      }),
      [inputValues]
    );

    return (
      <div className="my-3 bg-et-lightest-gray bc-bg-light-gray rounded">
        <div className="p-3 d-flex justify-content-between align-items-center bb-1-light-gray">
          <span>{t("Title", { taskType: taskType?.Title })}</span>
          <FieldTypeContainer onFieldTypeClick={handleFieldTypeClick} />
        </div>
        <div className="field-type-list p-3">
          {inputValues?.length === 0 ? (
            <InfoBar
              variant="information"
              iconCss="fa-outline fa-wand-magic-sparkles"
              message={t("AddYourFirstField")}
              rounded
            />
          ) : (
            <SortableListWithChildren
              id={"FieldTypeSortableList"}
              ref={componentRef}
              setItems={setInputValues}
              items={inputValues?.map((input) => input.Id)}
            >
              {inputValues?.map((inputValue, index) => {
                return (
                  <Fragment key={inputValue.Id}>
                    <Sortable id={inputValue.Id} disabled={false}>
                      <FieldTypeInputTemplate
                        index={index}
                        fieldType={inputValue.Type}
                        persistentValue={inputValue}
                        onInputChange={(value) => {
                          handleInputChange(
                            inputValue.Id,
                            value,
                            inputValue,
                            index
                          );
                        }}
                        onRemoveFieldType={() => {
                          setInputValues((prev) => {
                            const copy = [...prev];
                            copy.splice(index, 1);
                            return copy;
                          });
                        }}
                        {...rest}
                      />
                    </Sortable>
                  </Fragment>
                );
              })}
            </SortableListWithChildren>
          )}
        </div>
      </div>
    );
  }
);
